import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, CardHeader, ErrorNotice, LoadingState, Panel, Pill } from '@bcecloud/portal-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { errorMessage } from '@/lib/api';
import { STANDARD_PORTAL_FEATURES_SETTING, saveSystemSettings } from '@/lib/events';
import { readPortalFeatureDefaults, type PortalFeatureFlags } from '@/lib/portal-features';

/**
 * What the standard client portal shows by default. Customers use that
 * portal today, so a new feature stays off here until the team is ready,
 * and an event can still switch it on for itself. Events on an enterprise
 * portal show everything and are not governed here.
 */
export function ClientPortalDefaultsTab(): React.ReactElement {
  const queryClient = useQueryClient();
  const defaults = useQuery({
    queryKey: ['portal-feature-defaults'],
    queryFn: ({ signal }) => readPortalFeatureDefaults(signal),
  });
  const [draft, setDraft] = useState<PortalFeatureFlags | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (flags: PortalFeatureFlags) =>
      saveSystemSettings([{ name: STANDARD_PORTAL_FEATURES_SETTING, value: flags }]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['portal-feature-defaults'] });
      setDraft(null);
      setSaved(true);
    },
  });

  if (defaults.isPending) return <LoadingState label="Loading portal defaults…" />;
  if (defaults.isError) {
    return <ErrorNotice title="Portal defaults unavailable" message={errorMessage(defaults.error)} />;
  }
  const { catalogue } = defaults.data;
  const stored = defaults.data.defaults;
  const values = draft ?? stored;
  const isOn = (key: string, fallback: boolean): boolean => values[key] ?? fallback;
  const dirty = draft !== null && catalogue.some((feature) => isOn(feature.key, feature.defaultOn) !== (stored[feature.key] ?? feature.defaultOn));

  function set(key: string, on: boolean): void {
    setSaved(false);
    setDraft({ ...values, [key]: on });
  }

  return (
    <div className="mt-6 space-y-5">
      <Panel className="overflow-hidden">
        <div className="p-5 pb-4">
          <CardHeader
            className="mb-0"
            title="Standard client portal"
            description="What every event's client portal shows unless the event says otherwise. Events on an enterprise portal show everything and are not governed here."
          />
        </div>
        <ul className="divide-y divide-portal-line border-t border-portal-line">
          {catalogue.map((feature) => {
            const id = `portal-default-${feature.key}`;
            return (
              <li key={feature.key} className="flex items-start gap-3 px-5 py-3">
                <Checkbox
                  id={id}
                  checked={isOn(feature.key, feature.defaultOn)}
                  disabled={save.isPending}
                  onCheckedChange={(state) => set(feature.key, state === true)}
                  className="mt-0.5"
                />
                <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-portal-small font-bold">{feature.label}</span>
                    {feature.available ? null : <Pill tone="neutral">Not on the standard portal yet</Pill>}
                  </span>
                  <span className="block text-portal-micro text-portal-muted">{feature.description}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </Panel>

      {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}

      <div className="flex items-center gap-3">
        <Button
          disabled={save.isPending || !dirty}
          onClick={() =>
            save.mutate(
              Object.fromEntries(catalogue.map((feature) => [feature.key, isOn(feature.key, feature.defaultOn)])),
            )
          }
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="secondary" disabled={save.isPending || !dirty} onClick={() => setDraft(null)}>
          Reset
        </Button>
        {saved && !dirty ? <span className="text-portal-micro font-bold text-emerald-600">Saved</span> : null}
      </div>
    </div>
  );
}
